import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import AdminLayout from "./AdminLayout";
import ApiService from "../../services/api";
import {
  Plus,
  Edit,
  Trash2,
  Search,
  Filter,
  Building2,
  Users,
  UserCheck,
  UserX,
  Eye,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Globe,
  Palette,
  ExternalLink,
  Image,
  Settings,
  ChevronDown,
  Package,
  UserPlus,
  MoreVertical,
} from "lucide-react";

interface Clinic {
  clinic_id: number;
  external_clinic_id: string;
  clinic_name: string;
  legal_name: string;
  registration_no: string;
  tax_id: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  email: string;
  phone_number: string;
  timezone: string;
  notes?: string;
  is_active: boolean;
  created_at: string;
  white_label_settings: {
    brand_name: string;
    logo?: string | undefined;
    logo_url?: string;
    primary_color: string;
    subdomain: string;
    email: string;
    app_password: string;
    about_link: string;
    contact_link: string;
    terms_of_service_link: string;
    privacy_statement_link: string;
  };
}

interface ExternalClinic {
  _id: string;
  account_name: string;
}

interface ClinicOverview {
  total_accounts: number;
  active_accounts: number;
  total_users: number;
  pending_patients: number;
}

interface ClinicFormData {
  external_clinic_id: string;
  clinic_name: string;
  legal_name: string;
  registration_no: string;
  tax_id: string;
  street_address: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  email: string;
  phone_number: string;
  timezone: string;
  preferred_language: string;
  notes: string;
  white_label_settings: {
    brand_name: string;
    logo?: string;
    logo_url?: string;
    primary_color: string;
    subdomain: string;
    email: string;
    app_password: string;
    about_link: string;
    contact_link: string;
    terms_of_service_link: string;
    privacy_statement_link: string;
  };
}

const AdminAccounts: React.FC = () => {
  const [searchParams] = useSearchParams();
  const isAdd = searchParams.get("is_add") === "true";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [openDropdowns, setOpenDropdowns] = useState<Set<number>>(new Set());
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [externalClinics, setExternalClinics] = useState<ExternalClinic[]>([]);
  const [overview, setOverview] = useState<ClinicOverview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<
    "all" | "active" | "inactive"
  >("all");
  const [showModal, setShowModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState<{
    clinicId: number;
    currentStatus: boolean;
    clinicName: string;
  } | null>(null);
  const [editingClinic, setEditingClinic] = useState<Clinic | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const pageSize = 10;

  const [formData, setFormData] = useState<ClinicFormData>({
    external_clinic_id: "",
    clinic_name: "",
    legal_name: "",
    registration_no: "",
    tax_id: "",
    street_address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "USA",
    email: "",
    phone_number: "",
    timezone: "America/New_York",
    preferred_language: "en",
    notes: "",
    white_label_settings: {
      brand_name: "",
      logo: "",
      logo_url: "",
      primary_color: "#2241ec",
      subdomain: "",
      email: "",
      app_password: "",
      about_link: "",
      contact_link: "",
      terms_of_service_link: "",
      privacy_statement_link: "",
    },
  });

  useEffect(() => {
    fetchClinics();
    fetchOverview();
    fetchExternalClinics();
  }, [currentPage, searchTerm, filterStatus]);

  const fetchClinics = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: any = {
        page: currentPage,
        pageSize: pageSize,
      };

      // Add search term if provided
      if (searchTerm.trim()) {
        params.search_text = searchTerm.trim();
      }

      // Add status filter if not 'all'
      if (filterStatus !== "all") {
        params.is_active = filterStatus === "active" ? 1 : 0;
      }

      const response = await ApiService.searchClinics(params);

      if (response.success) {
        setClinics(response.data);
        setTotalPages(response.totalPages);
        setTotalItems(response.total);
      } else {
        throw new Error(response.message || "Failed to fetch accounts");
      }
    } catch (err: any) {
      console.error("Error fetching accounts:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch accounts"
      );
      setClinics([]);
      setTotalPages(1);
      setTotalItems(0);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await ApiService.getClinicOverview();
      if (response.success) {
        setOverview(response.data);
      }
    } catch (error) {
      console.error("Error fetching overview:", error);
    }
  };

  const fetchExternalClinics = async () => {
    try {
      const response = await ApiService.getExternalClinics();
      if (response.message === "Data Retrieved") {
        setExternalClinics(response.data);
      }
    } catch (error) {
      console.error("Error fetching external accounts:", error);
    }
  };

  const handleCreateClinic = () => {
    setEditingClinic(null);
    setFormData({
      external_clinic_id: "",
      clinic_name: "",
      legal_name: "",
      registration_no: "",
      tax_id: "",
      street_address: "",
      city: "",
      state: "",
      postal_code: "",
      country: "USA",
      email: "",
      phone_number: "",
      timezone: "America/New_York",
      preferred_language: "en",
      notes: "",
      white_label_settings: {
        brand_name: "",
        logo: "",
        logo_url: "",
        primary_color: "#2241ec",
        subdomain: "",
        email: "",
        app_password: "",
        about_link: "",
        contact_link: "",
        terms_of_service_link: "",
        privacy_statement_link: "",
      },
    });
    setShowModal(true);
  };

  useEffect(() => {
    if (isAdd) {
      handleCreateClinic();
    }
  }, [isAdd]);

  const toggleDropdown = (clinicId: number) => {
    setOpenDropdowns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(clinicId)) {
        newSet.delete(clinicId);
      } else {
        newSet.add(clinicId);
      }
      return newSet;
    });
  };

  const isDropdownOpen = (clinicId: number) => {
    return openDropdowns.has(clinicId);
  };

  const handleViewClinicDetails = (clinic_id: number, defaultTab = "") => {
    if (user?.role === "admin") {
      navigate(
        "/admin/accounts/" +
          clinic_id +
          (defaultTab?.length ? "?active_tab=" + defaultTab : "")
      );
    } else {
      navigate(
        "/account/accounts/" +
          clinic_id +
          (defaultTab?.length ? "?active_tab=" + defaultTab : "")
      );
    }
  };

  const handleEditClinic = (clinic: Clinic) => {
    setEditingClinic(clinic);
    setFormData({
      external_clinic_id: clinic.external_clinic_id,
      clinic_name: clinic.clinic_name,
      legal_name: clinic.legal_name,
      registration_no: clinic.registration_no,
      tax_id: clinic.tax_id,
      street_address: clinic.street_address,
      city: clinic.city,
      state: clinic.state,
      postal_code: clinic.postal_code,
      country: clinic.country,
      email: clinic.email,
      phone_number: clinic.phone_number,
      timezone: clinic.timezone,
      preferred_language: "en", // Default since not in response
      notes: clinic.notes || "",
      white_label_settings: clinic.white_label_settings,
    });
    setShowModal(true);
  };

  const handleStatusToggle = async (
    clinicId: number,
    currentStatus: boolean,
    clinicName: string
  ) => {
    setConfirmAction({ clinicId, currentStatus, clinicName });
    setShowConfirmModal(true);
  };

  const executeStatusToggle = async () => {
    if (!confirmAction) return;

    const { clinicId, currentStatus } = confirmAction;
    const action = currentStatus ? "deactivate" : "activate";

    try {
      await ApiService.updateClinicStatus(clinicId, action);
      fetchClinics();
      fetchOverview();
      setShowConfirmModal(false);
      setConfirmAction(null);
    } catch (err: any) {
      console.error("Error updating account status:", error);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to update account status"
      );
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingClinic) {
        await ApiService.updateClinic({
          clinic_id: editingClinic.clinic_id,
          ...formData,
        });
      } else {
        await ApiService.onboardClinic(formData);
      }

      setShowModal(false);
      fetchClinics();
      fetchOverview();
    } catch (err: any) {
      console.error("Error saving clinic:", error);
      setError(
        err?.response?.data?.message || err?.message || "Failed to save account"
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilterChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setFilterStatus(e.target.value as "all" | "active" | "inactive");
    setCurrentPage(1); // Reset to first page when filtering
  };

  return (
    <AdminLayout title="Accounts">
      <div className="space-y-4 sm:space-y-6 min-w-0 overflow-hidden w-full max-w-full bg-gray-50">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4">
            <div className="flex">
              <div className="text-sm text-red-700 flex-1">{error}</div>
              <button
                onClick={() => setError(null)}
                className="ml-2 sm:ml-auto text-red-700 hover:text-red-900 flex-shrink-0"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex flex-col space-y-3 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              Accounts
            </h1>
            <p className="text-sm sm:text-base text-gray-600">
              Total {totalItems}
            </p>
          </div>
          <button
            onClick={handleCreateClinic}
            className={`${
              user?.role === "admin" ? "" : "hidden"
            } inline-flex items-center justify-center px-3 py-2 sm:px-4 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors w-full sm:w-auto`}
          >
            <Plus className="h-4 w-4 mr-2" />
            Onboard
          </button>
        </div>

        {/* Stats Cards */}
        {overview && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 w-full max-w-full">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 sm:h-12 sm:w-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Total Accounts
                  </p>
                  <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                    {overview.total_accounts}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 sm:h-12 sm:w-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserCheck className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Active Accounts
                  </p>
                  <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                    {overview.active_accounts}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 sm:h-12 sm:w-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="h-4 w-4 sm:h-6 sm:w-6 text-orange-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Total Users
                  </p>
                  <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                    {overview.total_users}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-6">
              <div className="flex items-center">
                <div className="h-8 w-8 sm:h-12 sm:w-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <UserX className="h-4 w-4 sm:h-6 sm:w-6 text-yellow-600" />
                </div>
                <div className="ml-2 sm:ml-4 min-w-0">
                  <p className="text-xs sm:text-sm text-gray-600 truncate">
                    Pending Patients
                  </p>
                  <p className="text-lg sm:text-2xl font-semibold text-gray-900">
                    {overview.pending_patients}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col space-y-3 lg:flex-row lg:space-y-0 lg:gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search accounts..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="w-full lg:w-48">
              <select
                value={filterStatus}
                onChange={handleStatusFilterChange}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* Clinics Table */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 min-w-0 overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center items-center h-32 sm:h-64">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="overflow-x-auto px-0">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="pl-4 pr-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Account Details
                    </th>
                    <th className="hidden lg:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="hidden xl:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      White Label
                    </th>
                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="hidden md:table-cell px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="pl-3 pr-4 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {clinics.map((clinic) => (
                    <React.Fragment key={clinic.clinic_id}>
                      <tr className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4">
                          <div className="flex items-start sm:items-center">
                            <div className="h-8 w-8 sm:h-10 sm:w-10 bg-blue-100 rounded-lg flex items-center justify-center mr-2 sm:mr-4 flex-shrink-0">
                              <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {clinic.clinic_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 truncate">
                                {clinic.legal_name}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 flex items-center">
                                <Mail className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">{clinic.email}</span>
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 flex items-center">
                                <Phone className="h-3 w-3 mr-1 flex-shrink-0" />
                                <span className="truncate">
                                  {clinic.phone_number}
                                </span>
                              </div>
                            </div>
                          </div>
                        </td>

                        <td className="hidden lg:table-cell px-3 sm:px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {clinic.street_address}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <MapPin className="h-3 w-3 mr-1" />
                            {clinic.city}, {clinic.state} {clinic.postal_code}
                          </div>
                          <div className="text-sm text-gray-500">
                            {clinic.country}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center">
                            <Globe className="h-3 w-3 mr-1" />
                            {clinic.timezone}
                          </div>
                        </td>

                        <td className="hidden xl:table-cell px-3 sm:px-6 py-4">
                          <div className="text-sm text-gray-900 flex items-center">
                            <Palette className="h-3 w-3 mr-1" />
                            {clinic.white_label_settings.brand_name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {clinic.white_label_settings.subdomain}
                            .gattacagenomics.com
                          </div>
                          <div
                            className="w-4 h-4 rounded mt-1"
                            style={{
                              backgroundColor:
                                clinic.white_label_settings.primary_color,
                            }}
                            title={clinic.white_label_settings.primary_color}
                          />
                        </td>

                        <td className="px-3 sm:px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2 py-1 sm:px-2.5 sm:py-0.5 rounded-full text-xs font-medium ${
                              clinic.is_active
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {clinic.is_active ? "Active" : "Inactive"}
                          </span>
                          {/* Mobile info dropdown trigger */}
                          <div className="xl:hidden mt-2">
                            <button
                              onClick={() => toggleDropdown(clinic.clinic_id)}
                              className="inline-flex items-center px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded hover:bg-gray-200 transition-colors"
                            >
                              More Info
                              <ChevronDown
                                className={`h-3 w-3 ml-1 transition-transform duration-200 ${
                                  isDropdownOpen(clinic.clinic_id)
                                    ? "rotate-180"
                                    : "rotate-0"
                                }`}
                              />
                            </button>
                          </div>
                        </td>

                        <td className="hidden md:table-cell px-3 sm:px-6 py-4 text-sm text-gray-500">
                          {formatDate(clinic.created_at)}
                        </td>

                        <td className="px-3 sm:px-6 py-4">
                          <div className="flex items-center justify-end space-x-1 sm:space-x-2">
                            <button
                              onClick={() =>
                                handleViewClinicDetails(clinic.clinic_id)
                              }
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleViewClinicDetails(clinic.clinic_id, "users")
                              }
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Users"
                            >
                              <Users className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleViewClinicDetails(clinic.clinic_id, "products")
                              }
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Products"
                            >
                              <Package className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleViewClinicDetails(clinic.clinic_id, "patients")
                              }
                              className="text-blue-600 hover:text-blue-900 p-1"
                              title="Patients"
                            >
                              <UserPlus className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditClinic(clinic)}
                              className={`${
                                user?.role === "admin" ? "" : "hidden"
                              } text-blue-600 hover:text-blue-900 p-1`}
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                handleStatusToggle(
                                  clinic.clinic_id,
                                  clinic.is_active,
                                  clinic.clinic_name
                                )
                              }
                              className={`p-1 ${
                                user?.role === "admin" ? "" : "hidden"
                              } ${
                                clinic.is_active
                                  ? "text-red-600 hover:text-red-900"
                                  : "text-green-600 hover:text-green-900"
                              }`}
                              title={
                                clinic.is_active ? "Deactivate" : "Activate"
                              }
                            >
                              {clinic.is_active ? (
                                <UserX className="h-4 w-4" />
                              ) : (
                                <UserCheck className="h-4 w-4" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* Mobile dropdown row */}
                      {isDropdownOpen(clinic.clinic_id) && (
                        <tr className="xl:hidden bg-gray-50">
                          <td colSpan={6} className="px-3 sm:px-6 py-4">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              {/* Location Information */}
                              <div className="lg:hidden">
                                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                  <MapPin className="h-4 w-4 mr-1 text-gray-600" />
                                  Location
                                </h4>
                                <div className="space-y-1">
                                  <div className="text-sm text-gray-700">
                                    {clinic.street_address}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {clinic.city}, {clinic.state}{" "}
                                    {clinic.postal_code}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {clinic.country}
                                  </div>
                                  <div className="text-sm text-gray-500 flex items-center">
                                    <Globe className="h-3 w-3 mr-1" />
                                    {clinic.timezone}
                                  </div>
                                </div>
                              </div>

                              {/* White Label Information */}
                              <div className="xl:hidden">
                                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                  <Palette className="h-4 w-4 mr-1 text-gray-600" />
                                  White Label
                                </h4>
                                <div className="space-y-1">
                                  <div className="text-sm text-gray-700">
                                    {clinic.white_label_settings.brand_name}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {clinic.white_label_settings.subdomain}
                                    .gattacagenomics.com
                                  </div>
                                  <div className="flex items-center space-x-2">
                                    <div
                                      className="w-4 h-4 rounded border border-gray-300"
                                      style={{
                                        backgroundColor:
                                          clinic.white_label_settings
                                            .primary_color,
                                      }}
                                    />
                                    <span className="text-sm text-gray-500">
                                      {
                                        clinic.white_label_settings
                                          .primary_color
                                      }
                                    </span>
                                  </div>
                                </div>
                              </div>

                              {/* Created Date for smaller screens */}
                              <div className="md:hidden">
                                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                                  <Calendar className="h-4 w-4 mr-1 text-gray-600" />
                                  Created
                                </h4>
                                <div className="text-sm text-gray-500">
                                  {formatDate(clinic.created_at)}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>

              {clinics.length === 0 && !isLoading && (
                <div className="text-center py-8 sm:py-12 px-4">
                  <Building2 className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No accounts found
                  </h3>
                  <p className="mt-1 text-xs sm:text-sm text-gray-500">
                    {searchTerm || filterStatus !== "all"
                      ? "Try adjusting your search or filter criteria."
                      : "Get started by onboarding your first clinic."}
                  </p>
                  {!searchTerm && filterStatus === "all" && (
                    <button
                      onClick={handleCreateClinic}
                      className="mt-4 inline-flex items-center px-3 py-2 sm:px-4 text-sm bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Onboard Account
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 sm:px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() =>
                setCurrentPage((prev) => Math.min(totalPages, prev + 1))
              }
              disabled={currentPage === totalPages}
              className="px-3 py-2 sm:px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}

        {/* Confirmation Modal */}
        {showConfirmModal && confirmAction && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50 p-4">
            <div className="bg-white rounded-xl sm:rounded-2xl shadow-xl w-full max-w-md">
              <div className="p-4 sm:p-6">
                <div className="flex items-center mb-4">
                  <div
                    className={`h-10 w-10 sm:h-12 sm:w-12 rounded-full flex items-center justify-center mr-3 sm:mr-4 flex-shrink-0 ${
                      confirmAction.currentStatus
                        ? "bg-red-100"
                        : "bg-green-100"
                    }`}
                  >
                    {confirmAction.currentStatus ? (
                      <UserX className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                    ) : (
                      <UserCheck className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                      {confirmAction.currentStatus ? "Deactivate" : "Activate"}{" "}
                      Account
                    </h3>
                    <p className="text-xs sm:text-sm text-gray-500">
                      This action can be reversed later
                    </p>
                  </div>
                </div>

                <div className="mb-4 sm:mb-6">
                  <p className="text-sm sm:text-base text-gray-700">
                    Are you sure you want to{" "}
                    <span
                      className={`font-semibold ${
                        confirmAction.currentStatus
                          ? "text-red-600"
                          : "text-green-600"
                      }`}
                    >
                      {confirmAction.currentStatus ? "deactivate" : "activate"}
                    </span>{" "}
                    the Account{" "}
                    <span className="font-semibold text-gray-900">
                      "{confirmAction.clinicName}"
                    </span>
                    ?
                  </p>

                  <div
                    className={`mt-3 p-3 rounded-lg ${
                      confirmAction.currentStatus
                        ? "bg-red-50 border border-red-200"
                        : "bg-green-50 border border-green-200"
                    }`}
                  >
                    <p
                      className={`text-xs sm:text-sm ${
                        confirmAction.currentStatus
                          ? "text-red-700"
                          : "text-green-700"
                      }`}
                    >
                      {confirmAction.currentStatus
                        ? "This account will not be able to access the platform or process new patients."
                        : "This account will be able to access the platform and process patients."}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => {
                      setShowConfirmModal(false);
                      setConfirmAction(null);
                    }}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={executeStatusToggle}
                    className={`w-full sm:w-auto px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors ${
                      confirmAction.currentStatus
                        ? "bg-red-600 hover:bg-red-700"
                        : "bg-green-600 hover:bg-green-700"
                    }`}
                  >
                    {confirmAction.currentStatus ? "Deactivate" : "Activate"}{" "}
                    Account
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Clinic Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/7 backdrop-blur-sm bg-opacity-50 p-4 overflow-hidden">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit} className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6 ">
                  <h2 className="text-lg font-semibold ">
                    {editingClinic ? "Edit Account" : "Onboard New Account"}
                  </h2>
                  <button
                    onClick={() => setShowModal(false)}
                    className="text-sm cursor-pointer rounded-full p-0 text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                  >
                    X
                  </button>
                </div>

                <div className="space-y-4 sm:space-y-6">
                  {/* External Clinic Selection */}
                  {!editingClinic && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        External Account *
                      </label>
                      <select
                        value={formData.external_clinic_id}
                        onChange={(e) => {
                          const selectedClinic = externalClinics.find(
                            (c) => c._id === e.target.value
                          );
                          setFormData({
                            ...formData,
                            external_clinic_id: e.target.value,
                            clinic_name: selectedClinic?.account_name || "",
                            legal_name: selectedClinic?.account_name || "",
                            white_label_settings: {
                              ...formData.white_label_settings,
                              brand_name: selectedClinic?.account_name || "",
                            },
                          });
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="">Select External Account</option>
                        {externalClinics.map((clinic) => (
                          <option key={clinic._id} value={clinic._id}>
                            {clinic.account_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Basic Information */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Account Name *
                      </label>
                      <input
                        type="text"
                        value={formData.clinic_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            clinic_name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Legal Name *
                      </label>
                      <input
                        type="text"
                        value={formData.legal_name}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            legal_name: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Registration Number *
                      </label>
                      <input
                        type="text"
                        value={formData.registration_no}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            registration_no: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tax ID *
                      </label>
                      <input
                        type="text"
                        value={formData.tax_id}
                        onChange={(e) =>
                          setFormData({ ...formData, tax_id: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) =>
                          setFormData({ ...formData, email: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Phone Number *
                      </label>
                      <input
                        type="text"
                        value={formData.phone_number}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            phone_number: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Address */}
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-3">
                      Address
                    </h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Street Address *
                        </label>
                        <input
                          type="text"
                          value={formData.street_address}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              street_address: e.target.value,
                            })
                          }
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City *
                          </label>
                          <input
                            type="text"
                            value={formData.city}
                            onChange={(e) =>
                              setFormData({ ...formData, city: e.target.value })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State *
                          </label>
                          <input
                            type="text"
                            value={formData.state}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                state: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Postal Code *
                          </label>
                          <input
                            type="text"
                            value={formData.postal_code}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                postal_code: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Country *
                          </label>
                          <input
                            type="text"
                            value={formData.country}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                country: e.target.value,
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Timezone *
                      </label>
                      <select
                        value={formData.timezone}
                        onChange={(e) =>
                          setFormData({ ...formData, timezone: e.target.value })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="America/New_York">Eastern Time</option>
                        <option value="America/Chicago">Central Time</option>
                        <option value="America/Denver">Mountain Time</option>
                        <option value="America/Los_Angeles">
                          Pacific Time
                        </option>
                        <option value="Europe/London">London</option>
                        <option value="Europe/Berlin">Berlin</option>
                        <option value="Asia/Tokyo">Tokyo</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Preferred Language *
                      </label>
                      <select
                        value={formData.preferred_language}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            preferred_language: e.target.value,
                          })
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        required
                      >
                        <option value="en">English</option>
                        <option value="es">Spanish</option>
                        <option value="fr">French</option>
                        <option value="de">German</option>
                      </select>
                    </div>
                  </div>

                  {/* White Label Settings */}
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                      <Palette className="h-5 w-5 mr-2 text-blue-600" />
                      White Label Settings
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Brand Name *
                          </label>
                          <input
                            type="text"
                            value={formData.white_label_settings.brand_name}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  brand_name: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Subdomain *
                          </label>
                          <input
                            type="text"
                            value={formData.white_label_settings.subdomain}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  subdomain: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="subdomain"
                            required
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Primary Color *
                          </label>
                          <div className="flex space-x-2">
                            <input
                              type="color"
                              value={
                                formData.white_label_settings.primary_color
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  white_label_settings: {
                                    ...formData.white_label_settings,
                                    primary_color: e.target.value,
                                  },
                                })
                              }
                              className="w-12 sm:w-16 h-8 sm:h-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer flex-shrink-0"
                              required
                            />
                            <input
                              type="text"
                              value={
                                formData.white_label_settings.primary_color
                              }
                              onChange={(e) => {
                                let value = e.target.value;
                                // Ensure the value starts with #
                                if (value && !value.startsWith("#")) {
                                  value = "#" + value;
                                }
                                // Validate hex format (# followed by 3 or 6 hex characters)
                                if (
                                  value === "" ||
                                  /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(
                                    value
                                  )
                                ) {
                                  setFormData({
                                    ...formData,
                                    white_label_settings: {
                                      ...formData.white_label_settings,
                                      primary_color: value,
                                    },
                                  });
                                }
                              }}
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                              placeholder="#2241ec"
                              pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                              required
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">
                            Enter a HEX color code (e.g., #2241ec or #fff)
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Logo URL *
                          </label>
                          <div className="relative">
                            <Image className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="url"
                              value={
                                formData.white_label_settings.logo_url || ""
                              }
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  white_label_settings: {
                                    ...formData.white_label_settings,
                                    logo_url: e.target.value,
                                  },
                                })
                              }
                              className="w-full pl-10 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="https://example.com/logo.png"
                              required
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            About Link
                          </label>
                          <input
                            type="url"
                            value={formData.white_label_settings.about_link}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  about_link: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/about"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Link
                          </label>
                          <input
                            type="url"
                            value={formData.white_label_settings.contact_link}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  contact_link: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/contact"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Terms of Service Link
                          </label>
                          <input
                            type="url"
                            value={
                              formData.white_label_settings
                                .terms_of_service_link
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  terms_of_service_link: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/terms"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Privacy Statement Link
                          </label>
                          <input
                            type="url"
                            value={
                              formData.white_label_settings
                                .privacy_statement_link
                            }
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                white_label_settings: {
                                  ...formData.white_label_settings,
                                  privacy_statement_link: e.target.value,
                                },
                              })
                            }
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="https://example.com/privacy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Gmail SMTP Configuration */}
                  <div>
                    <h3 className="text-md font-medium text-gray-900 mb-3 flex items-center">
                      <Settings className="h-5 w-5 mr-2 text-green-600" />
                      Gmail SMTP Configuration
                    </h3>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Support Email *
                          </label>
                          <div className="relative">
                            <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="email"
                              value={formData.white_label_settings.email}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  white_label_settings: {
                                    ...formData.white_label_settings,
                                    email: e.target.value,
                                  },
                                })
                              }
                              className="w-full pl-10 pr-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                              required
                              placeholder="support@example.com"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            App Password *
                          </label>
                          <div className="relative">
                            <Settings className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                            <input
                              type="password"
                              value={formData.white_label_settings.app_password}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  white_label_settings: {
                                    ...formData.white_label_settings,
                                    app_password: e.target.value,
                                  },
                                })
                              }
                              className="w-full pl-10 pr-3 py-2 text-sm border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent bg-white"
                              required
                              placeholder="Gmail app password"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="mt-3">
                        <p className="text-xs sm:text-sm text-green-700">
                          <strong>Note:</strong> Use a Gmail app password for
                          secure email sending.
                          <a
                            href="https://support.google.com/accounts/answer/185833"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-800 underline hover:text-green-900 ml-1 inline-flex items-center"
                          >
                            Learn how to create an app password
                            <ExternalLink className="h-3 w-3 ml-1" />
                          </a>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      rows={3}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Additional notes about this lab..."
                    />
                  </div>
                </div>

                <div className="flex flex-col-reverse sm:flex-row sm:justify-end space-y-2 space-y-reverse sm:space-y-0 sm:space-x-4 mt-4 sm:mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {editingClinic ? "Update" : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminAccounts;
